import { useState, useEffect } from "react";
import { Anchor, LoadingShimmer } from "@newsela/angelou";

import Link from "next/link";
import { withTrackEvent, actionNames, objectTypes } from "@newsela/snitch";
import { useRouter } from "next/router";
import { useQuery } from "@tanstack/react-query";
import useInsightsSharedContext from "@/reports/teacher/utils/useInsightsSharedContext/useInsightsSharedContext";
import ReadingSkillsDataTable, {
  TableData,
} from "@/reports/components/ReadingSkillsDataTable";
import slugifyString from "@/utils/slugifyString";
import { GradeKey } from "@/reports/utils/gradeUtils";

import { API_BASE_URL } from "@/api/api.config";
import { ReactTableProps } from "@/reports/components/ReadingSkillsDataTable/ReadingSkillsDataTable";
import {
  ReadingSkillsEntity,
  SkillsPerformance,
  StudentActivityFilters,
} from "@/reports/types";
import {
  readingSkillsPerformanceMock,
  readingSkillsPerformanceQueries,
} from "@/reports/queries/readingSkillsPerformance";
import { ApiCatalog } from "@/api/catalogs/ApiCatalog";
import renderDataCellLabel from "@/reports/utils/renderDataCellLabel";
import shapeQuizPerformanceData from "@/reports/utils/shapeQuizPerformanceData";
import ContainerHeaderSection from "../../common/ContainerHeaderSection";
import { FullWidthLoadingError } from "../../common/FullWidthLoadingError";
import ReadingLevelFilter from "../../common/ReadingLevelFilter";
import { loadingColumnCss } from "../../common/ReportTable/components/ReadingActivityTableSkeleton/ReadingActivityTableSkeleton.style";
import { containerCss } from "./ClassroomReadingSkills.style";

type QuizPerformance =
  | Pick<SkillsPerformance, "average_question_score" | "student_count">
  | {};

const skillsPerformanceReducer = (
  accumulator: QuizPerformance,
  skill: SkillsPerformance,
) => {
  return {
    ...accumulator,
    [slugifyString(skill.skill_name)]: {
      average_quiz_score: skill.average_question_score,
      quiz_count: skill.question_count,
      student_count: skill.student_count,
    },
  };
};

const shapeReadingSkills = (data: SkillsPerformance[]) => {
  return data.map((skill: SkillsPerformance) => ({
    content_id: skill.skill_content_id,
    slug: slugifyString(skill.skill_name),
    text: skill.skill_name,
  }));
};

const shapeTableData = (entities: ReadingSkillsEntity): TableData[] => {
  const entitiesData = Object.entries(entities);
  return entitiesData?.map(([entityId, data]) => {
    const studentName = `${data.name.last}, ${data.name.first}`;
    const quizData = data.skills.reduce<QuizPerformance>(
      skillsPerformanceReducer,
      {},
    );
    const mappedQuizData = shapeQuizPerformanceData(quizData);
    return {
      studentUniqueId: entityId,
      student: studentName,
      ...mappedQuizData,
    };
  });
};

const shapeTableHeaderData = (newsela: SkillsPerformance[]): TableData => {
  const newselaAverageData = newsela.reduce<QuizPerformance>(
    skillsPerformanceReducer,
    {},
  );
  return shapeQuizPerformanceData(newselaAverageData);
};

const AnchorWithTrackEvent = withTrackEvent(Anchor);

type FilterOrder = StudentActivityFilters["activity_type_filter_order"];

const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_PAGE_INDEX = 1;

interface ClassroomReadingSkillsProps {
  useMockData: boolean;
}

const ClassroomReadingSkills = (props: ClassroomReadingSkillsProps) => {
  const { useMockData = false } = props;
  const router = useRouter();
  const queryString = new URLSearchParams(
    router.query as Record<string, string>,
  ).toString();
  const { readingSkillsInitialProps, selectedFilters } =
    useInsightsSharedContext();
  const {
    onlyAssignedContent,
    classroomUniqueId,
    dateRange,
    readingLevel: selectedReadingLevel,
  } = selectedFilters;
  const minDateFilter = dateRange[0];
  const maxDateFilter = dateRange[1];
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [pageIndex, setPageIndex] = useState(DEFAULT_PAGE_INDEX);
  const [readingLevel, setReadinglevel] = useState<GradeKey | -1>(-1);
  const readingLevelFilter =
    readingLevel >= 0
      ? {
          reading_level: String(readingLevel as GradeKey),
        }
      : {};
  const [filterActivityKey, setFilterActivityKey] = useState<string>();
  const [filterOrder, setFilterOrder] = useState<FilterOrder>();

  const filterOrderObj: {} = filterOrder
    ? {
        activity_type_filter: filterActivityKey,
        activity_type_filter_order: filterOrder,
      }
    : {};

  const onlyAssignedContentFilter =
    onlyAssignedContent !== null
      ? {
          only_assigned_content: String(onlyAssignedContent),
        }
      : {};

  const commonParams = {
    entity_type: "classroom" as const,
    entity_id: classroomUniqueId ?? "",
    min_date: minDateFilter,
    max_date: maxDateFilter,
    ...readingLevelFilter,
    ...onlyAssignedContentFilter,
  };

  const { data, isLoading, error } = useQuery(
    readingSkillsPerformanceQueries.filter(
      {
        ...commonParams,
        page_size: pageSize,
        page_index: pageIndex,
        ...filterOrderObj,
      },
      !useMockData,
    ),
  );

  const csvDownloadParams = new URLSearchParams({
    ...commonParams,
  }).toString();

  const downloadCsvUrl = `${API_BASE_URL}${ApiCatalog.readingSkillsPerformanceCsv.endpoint}?${csvDownloadParams}`;

  useEffect(() => {
    if (selectedReadingLevel) {
      const gradeString = selectedReadingLevel;
      if (gradeString) {
        const value = parseInt(gradeString) as GradeKey;
        setReadinglevel(value);
      }
    } else {
      setReadinglevel(-1);
    }
  }, [selectedReadingLevel]);

  if (error && !useMockData) return <FullWidthLoadingError />;

  const readingSkillsForLoadingState =
    readingSkillsInitialProps &&
    Object.entries(readingSkillsInitialProps).map(
      ([contentId, skillsProps]) => {
        return {
          average_question_score: 0,
          question_count: 0,
          skill_content_id: contentId,
          skill_integer: skillsProps.position as number,
          skill_name: skillsProps.skillName as string,
          student_count: 0,
        };
      },
    );
  const readingSkills =
    isLoading && readingSkillsForLoadingState
      ? shapeReadingSkills(readingSkillsForLoadingState)
      : shapeReadingSkills(data?.data?.data?.newsela ?? []);
  /*
    Mock data is used here to control the loading state of the reading skills table;
    The number of entities in the mock data sets the number of rows in the table during loading state.
  */
  const mockClassroomDataForLoadingState = shapeTableData(
    readingSkillsPerformanceMock.data.data.entities,
  );
  const classroomData = isLoading
    ? mockClassroomDataForLoadingState
    : shapeTableData(data?.data?.data?.entities ?? {});
  const newselaReadingSkillsData = shapeTableHeaderData(
    data?.data?.data?.newsela ?? [],
  );
  const totalResults = data ? data.data?.data?.total_items : 0;
  const streamingProps = {
    actionName: actionNames.navigate,
    objectType: objectTypes.fullPage,
    streamProperties: {
      description:
        "Teacher Reporting - classroom reading skills - navigate to student data from student name",
    },
  };

  const handleControlsUpdate = (tableProps: ReactTableProps) => {
    if (tableProps.sortBy.length > 0) {
      tableProps.sortBy.forEach((sortBy) => {
        const sortByKey =
          sortBy.id === "student"
            ? "student-name"
            : readingSkills.find((skill) => skill.slug === sortBy.id)
                ?.content_id;
        setFilterActivityKey(sortByKey);
        setFilterOrder(sortBy.desc ? "desc" : "asc");
      });
    } else {
      setFilterOrder(null);
    }
    setPageSize(tableProps.pageSize);
    setPageIndex(tableProps.pageIndex + 1);
  };

  return (
    <div css={containerCss}>
      <ReadingSkillsDataTable
        onControlsUpdate={handleControlsUpdate}
        firstColumn={{
          Header: "Student",
          accessor: "student",
          Cell: (cellProps) => {
            const { cell } = cellProps;
            const studentUniqueId = cell?.row?.original?.["studentUniqueId"];
            const href = `${API_BASE_URL}/reports/teacher/student-data/?${queryString}&student=${studentUniqueId}`;
            const studentName = String(cell.value);
            return (
              <>
                {isLoading || !cell ? (
                  <div css={loadingColumnCss}>
                    <LoadingShimmer />
                  </div>
                ) : (
                  <AnchorWithTrackEvent
                    href={href}
                    ariaProps={{
                      "aria-label": studentName,
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      router.push(href);
                    }}
                    {...streamingProps}
                    __renderCustomLink={({ href, ...anchorProps }) => {
                      return (
                        <Link href={href as URL} passHref legacyBehavior>
                          <a {...anchorProps}>{studentName}</a>
                        </Link>
                      );
                    }}
                  />
                )}
              </>
            );
          },
        }}
        headerCellLabel="Newsela average"
        headerData={newselaReadingSkillsData}
        renderTitleSection={() => (
          <>
            <ContainerHeaderSection
              title="Reading skills"
              explanationText="Use the table below to see student quiz performance in all 8 reading skills."
              downloadCSVProps={{
                downloadCsvFilename: "classroom-reading-skills-data.csv",
                downloadCsvUrl,
                ariaLabel: `Reading Skills Download as CSV`,
              }}
            />
            <ReadingLevelFilter isClassroomPage />
          </>
        )}
        renderDataCellLabel={renderDataCellLabel}
        tableData={classroomData}
        filterByReadingSkillSlug="filterByReadingSkillSlug"
        readingSkills={readingSkills}
        initialState={{
          pageSize,
          totalResults,
          pageCount: Math.ceil(totalResults / pageSize),
        }}
        isLoading={isLoading}
      />
    </div>
  );
};

export default ClassroomReadingSkills;
